import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Alert,
  Box,
  Card,
  Menu,
  Stack,
  Button,
  Switch,
  Divider,
  Tooltip,
  Container,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  MenuItem,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// hooks
import useSettings from '../../../hooks/useSettings';
// components
import Page from '../../../components/Page';
import Label from '../../../components/Label';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
// data
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import {
  AboutDraft,
  AboutSection,
  EMPTY_CONFIRM_TEXT,
  LEAVE_TEXT,
  LOAD_FAILED_TEXT,
  UPLOAD_WAIT_TEXT,
  aboutErrorText,
  addListItem,
  addSection,
  adoptCids,
  draftStatus,
  draftTime,
  isAboutChanged,
  isDirty,
  moveByCid,
  needsEmptyConfirm,
  readAnswer,
  removeByCid,
  removeListItemByCid,
  savePayload,
  savedText,
  setDataByCid,
  setListItemByCid,
  setSectionByCid,
  snapshotOf,
  withCids,
} from './aboutModel';
import useLeaveGuard from './useLeaveGuard';
import useAboutDraft, { clearDraft, readDraft } from './useAboutDraft';

// ---------------------------------------------------------------------------
// Section model. The public /about-us page (kayfa-nextjs AboutDynamic.tsx)
// renders each section by `type`; the `data` shape per type is described by the
// schemas below. Array order === display order (persisted as `order` on save).
// The block type and the editor's helpers live in aboutModel.ts.
// ---------------------------------------------------------------------------

type FieldType = 'text' | 'textarea' | 'image' | 'url';
type Field = { key: string; label: string; type: FieldType; rtl?: boolean; help?: string };
type ListSchema = { key: string; itemLabel: string; addLabel: string; fields: Field[] };
type SectionSchema = {
  type: string;
  label: string;
  desc: string;
  icon: string;
  fields: Field[];
  list?: ListSchema;
};


const EN = (k: string, l: string): Field => ({ key: k, label: l, type: 'text' });
const AR = (k: string, l: string): Field => ({ key: k, label: l, type: 'text', rtl: true });
const TA = (k: string, l: string): Field => ({ key: k, label: l, type: 'textarea' });
const TA_AR = (k: string, l: string): Field => ({ key: k, label: l, type: 'textarea', rtl: true });

const SCHEMAS: SectionSchema[] = [
  {
    type: 'hero',
    label: 'Hero banner',
    desc: 'Top banner with headline, subtitle, background image and a call-to-action button.',
    icon: 'eva:image-outline',
    fields: [
      EN('title', 'Title (EN)'),
      AR('title_ar', 'Title (AR)'),
      TA('subtitle', 'Subtitle (EN)'),
      TA_AR('subtitle_ar', 'Subtitle (AR)'),
      { key: 'image', label: 'Background image', type: 'image' },
      EN('ctaText', 'Button text (EN)'),
      AR('ctaText_ar', 'Button text (AR)'),
      { key: 'ctaLink', label: 'Button link', type: 'url' },
    ],
  },
  {
    type: 'mission',
    label: 'Mission',
    desc: 'Heading + paragraph with an optional side image.',
    icon: 'eva:flag-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)'), TA('body', 'Body (EN)'), TA_AR('body_ar', 'Body (AR)'), { key: 'image', label: 'Image', type: 'image' }],
  },
  {
    type: 'vision',
    label: 'Vision',
    desc: 'Heading + paragraph with an optional side image.',
    icon: 'eva:eye-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)'), TA('body', 'Body (EN)'), TA_AR('body_ar', 'Body (AR)'), { key: 'image', label: 'Image', type: 'image' }],
  },
  {
    type: 'coreValues',
    label: 'Core Values',
    desc: 'A grid of value cards, each with an emoji/icon, title and description.',
    icon: 'eva:award-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)')],
    list: {
      key: 'items',
      itemLabel: 'Value',
      addLabel: 'Add value',
      fields: [{ key: 'icon', label: 'Emoji / icon', type: 'text', help: 'e.g. 🎯' }, EN('title', 'Title (EN)'), AR('title_ar', 'Title (AR)'), TA('text', 'Text (EN)'), TA_AR('text_ar', 'Text (AR)')],
    },
  },
  {
    type: 'learningModel',
    label: 'Learning Model',
    desc: 'A numbered list of steps describing how learning works.',
    icon: 'eva:list-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)')],
    list: {
      key: 'steps',
      itemLabel: 'Step',
      addLabel: 'Add step',
      fields: [EN('title', 'Title (EN)'), AR('title_ar', 'Title (AR)'), TA('text', 'Text (EN)'), TA_AR('text_ar', 'Text (AR)')],
    },
  },
  {
    type: 'partners',
    label: 'Partners / Sponsors',
    desc: 'A row of partner or sponsor logos, each optionally linking out.',
    icon: 'eva:briefcase-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)')],
    list: {
      key: 'items',
      itemLabel: 'Partner',
      addLabel: 'Add partner',
      fields: [EN('name', 'Name'), { key: 'logo', label: 'Logo', type: 'image' }, { key: 'link', label: 'Website link', type: 'url' }],
    },
  },
  {
    type: 'team',
    label: 'Team / Leadership',
    desc: 'Team member cards with photo, name, role, short bio and LinkedIn.',
    icon: 'eva:people-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)')],
    list: {
      key: 'members',
      itemLabel: 'Member',
      addLabel: 'Add member',
      fields: [
        { key: 'photo', label: 'Photo', type: 'image' },
        EN('name', 'Name (EN)'),
        AR('name_ar', 'Name (AR)'),
        EN('role', 'Role (EN)'),
        AR('role_ar', 'Role (AR)'),
        TA('bio', 'Bio (EN)'),
        TA_AR('bio_ar', 'Bio (AR)'),
        { key: 'linkedin', label: 'LinkedIn URL', type: 'url' },
      ],
    },
  },
  {
    type: 'testimonials',
    label: 'Testimonials',
    desc: 'Quote cards with the author’s photo, name and role.',
    icon: 'eva:message-square-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)')],
    list: {
      key: 'items',
      itemLabel: 'Testimonial',
      addLabel: 'Add testimonial',
      fields: [
        { key: 'photo', label: 'Photo', type: 'image' },
        EN('name', 'Name (EN)'),
        AR('name_ar', 'Name (AR)'),
        EN('role', 'Role (EN)'),
        AR('role_ar', 'Role (AR)'),
        TA('quote', 'Quote (EN)'),
        TA_AR('quote_ar', 'Quote (AR)'),
      ],
    },
  },
  {
    type: 'accreditations',
    label: 'Accreditations / Awards',
    desc: 'Badges/logos of accreditations or awards, each optionally linking out.',
    icon: 'eva:shield-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)')],
    list: {
      key: 'items',
      itemLabel: 'Item',
      addLabel: 'Add item',
      fields: [{ key: 'image', label: 'Badge / logo', type: 'image' }, EN('title', 'Title (EN)'), AR('title_ar', 'Title (AR)'), { key: 'link', label: 'Link', type: 'url' }],
    },
  },
  {
    type: 'milestones',
    label: 'Journey / Milestones',
    desc: 'A timeline of milestones, each with a year, title and description.',
    icon: 'eva:calendar-outline',
    fields: [EN('heading', 'Heading (EN)'), AR('heading_ar', 'Heading (AR)')],
    list: {
      key: 'items',
      itemLabel: 'Milestone',
      addLabel: 'Add milestone',
      fields: [{ key: 'year', label: 'Year', type: 'text', help: 'e.g. 2019' }, EN('title', 'Title (EN)'), AR('title_ar', 'Title (AR)'), TA('text', 'Text (EN)'), TA_AR('text_ar', 'Text (AR)')],
    },
  },
];

const schemaFor = (type: string) => SCHEMAS.find((s) => s.type === type);

// ---------------------------------------------------------------------------
// Small reusable image field: uploads to /files/media, stores the returned URL.
// `beginUpload` counts the upload in the editor (moves, removes and Save wait
// for it); the URL is written through `onChange`, which targets the block or
// item by its client id, so a late answer never lands in another block.
// ---------------------------------------------------------------------------
function ImageField({
  label,
  value,
  onChange,
  disabled,
  beginUpload,
}: {
  label: string;
  value?: string;
  onChange: (url: string) => void;
  disabled?: boolean;
  beginUpload: () => () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);

  const upload = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    const end = beginUpload();
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
      const url = res.data?.url ?? res.data?.data?.url;
      if (!url) throw new Error('No URL returned');
      onChange(url);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Upload failed', { variant: 'error' });
    } finally {
      end();
      setBusy(false);
    }
  };

  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Stack direction="row" spacing={1.5} alignItems="center">
        {value ? (
          <Box
            component="img"
            src={value}
            alt=""
            sx={{ width: 56, height: 56, borderRadius: 1, objectFit: 'cover', border: '1px solid', borderColor: 'divider' }}
          />
        ) : (
          <Box sx={{ width: 56, height: 56, borderRadius: 1, bgcolor: 'background.neutral', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Iconify icon="eva:image-outline" sx={{ color: 'text.disabled' }} />
          </Box>
        )}
        <Button component="label" size="small" variant="outlined" disabled={busy || disabled} startIcon={busy ? <CircularProgress size={14} /> : <Iconify icon="eva:cloud-upload-outline" />}>
          {value ? 'Replace' : 'Upload'}
          <input hidden type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} />
        </Button>
        {value ? (
          <IconButton size="small" color="error" disabled={busy || disabled} onClick={() => onChange('')} aria-label={`Remove ${label}`}>
            <Iconify icon="eva:close-outline" />
          </IconButton>
        ) : null}
      </Stack>
    </Stack>
  );
}

// Render one field (text/textarea/url/image) bound to an object + key.
function FieldInput({
  field,
  obj,
  onChange,
  disabled,
  beginUpload,
}: {
  field: Field;
  obj: Record<string, any>;
  onChange: (key: string, value: any) => void;
  disabled?: boolean;
  beginUpload: () => () => void;
}) {
  if (field.type === 'image') {
    return (
      <ImageField
        label={field.label}
        value={obj[field.key] || ''}
        onChange={(url) => onChange(field.key, url)}
        disabled={disabled}
        beginUpload={beginUpload}
      />
    );
  }
  return (
    <TextField
      label={field.label}
      value={obj[field.key] || ''}
      onChange={(e) => onChange(field.key, e.target.value)}
      fullWidth
      size="small"
      disabled={disabled}
      multiline={field.type === 'textarea'}
      minRows={field.type === 'textarea' ? 2 : undefined}
      helperText={field.help}
      inputProps={field.rtl ? { dir: 'rtl' } : undefined}
    />
  );
}

// ---------------------------------------------------------------------------
// One block. Every update targets the block (and list item) by its client id.
// ---------------------------------------------------------------------------

type SetSections = Dispatch<SetStateAction<AboutSection[]>>;

function SectionCard({
  section,
  index,
  count,
  locked,
  lockNote,
  saving,
  beginUpload,
  setSections,
}: {
  section: AboutSection;
  index: number;
  count: number;
  // Structure controls (move, toggle, remove, add) — off while saving or uploading.
  locked: boolean;
  // Why they are off (the tooltip), e.g. "Waiting for the upload…".
  lockNote: string;
  // Fields — off while saving.
  saving: boolean;
  beginUpload: () => () => void;
  setSections: SetSections;
}) {
  const schema = schemaFor(section.type);
  const enabled = section.enabled !== false;
  const cid = section._cid || '';
  const name = schema?.label || section.type;
  const list = schema?.list;
  const items: Record<string, any>[] = list && Array.isArray(section.data?.[list.key]) ? section.data?.[list.key] : [];
  const waitTip = (tip: string) => (locked && lockNote ? lockNote : tip);

  return (
    <Card sx={{ p: 2.5, opacity: enabled ? 1 : 0.6 }} data-about-block={name}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <Iconify icon={schema?.icon || 'eva:cube-outline'} sx={{ width: 24, height: 24, color: 'primary.main' }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1">{name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {schema?.desc || 'Unknown section type'}
          </Typography>
        </Box>
        <Label color={enabled ? 'success' : 'default'}>{enabled ? 'Visible' : 'Hidden'}</Label>
        <Tooltip title={enabled ? 'Hide from site' : 'Show on site'}>
          <span>
            <Switch
              checked={enabled}
              disabled={locked}
              inputProps={{ 'aria-label': `Show ${name} on the site` }}
              onChange={(e) => setSections((prev) => setSectionByCid(prev, cid, { enabled: e.target.checked }))}
            />
          </span>
        </Tooltip>
        <Tooltip title={waitTip('Move up')}>
          <span>
            <IconButton size="small" disabled={locked || index === 0} aria-label={`Move ${name} up`} onClick={() => setSections((prev) => moveByCid(prev, cid, -1))}>
              <Iconify icon="eva:arrow-upward-outline" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={waitTip('Move down')}>
          <span>
            <IconButton size="small" disabled={locked || index === count - 1} aria-label={`Move ${name} down`} onClick={() => setSections((prev) => moveByCid(prev, cid, 1))}>
              <Iconify icon="eva:arrow-downward-outline" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={waitTip('Remove section')}>
          <span>
            <IconButton size="small" color="error" disabled={locked} aria-label={`Remove ${name}`} onClick={() => setSections((prev) => removeByCid(prev, cid))}>
              <Iconify icon="eva:trash-2-outline" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {!schema ? (
        <Typography variant="body2" color="error">
          This section type (“{section.type}”) is not recognised by this editor version.
        </Typography>
      ) : (
        <>
          <Divider sx={{ my: 1.5 }} />
          {/* top-level fields */}
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            {schema.fields.map((f) => (
              <Box key={f.key} sx={{ gridColumn: f.type === 'textarea' ? '1 / -1' : undefined }}>
                <FieldInput
                  field={f}
                  obj={section.data || {}}
                  disabled={saving}
                  beginUpload={beginUpload}
                  onChange={(k, v) => setSections((prev) => setDataByCid(prev, cid, k, v))}
                />
              </Box>
            ))}
          </Box>

          {/* repeatable list */}
          {list ? (
            <Box sx={{ mt: 2.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">{list.itemLabel}s</Typography>
                <Button size="small" disabled={locked} startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => setSections((prev) => addListItem(prev, cid, list.key))}>
                  {list.addLabel}
                </Button>
              </Stack>
              <Stack spacing={1.5}>
                {items.map((item, itemIdx) => {
                  const itemCid = item._cid || '';
                  return (
                    <Box key={itemCid || itemIdx} sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', position: 'relative' }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {list.itemLabel} #{itemIdx + 1}
                        </Typography>
                        <Tooltip title={waitTip(`Remove ${list.itemLabel.toLowerCase()}`)}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={locked}
                              aria-label={`Remove ${list.itemLabel} ${itemIdx + 1}`}
                              onClick={() => setSections((prev) => removeListItemByCid(prev, cid, list.key, itemCid))}
                            >
                              <Iconify icon="eva:close-outline" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                        {list.fields.map((f) => (
                          <Box key={f.key} sx={{ gridColumn: f.type === 'textarea' ? '1 / -1' : undefined }}>
                            <FieldInput
                              field={f}
                              obj={item || {}}
                              disabled={saving}
                              beginUpload={beginUpload}
                              onChange={(k, v) => setSections((prev) => setListItemByCid(prev, cid, list.key, itemCid, k, v))}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
                {items.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                    No {list.itemLabel.toLowerCase()}s yet.
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          ) : null}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// The editor (plan R6 AB-1..AB-6). Changes go live only on Save. Nothing can be
// saved before one load succeeded (a failed load never empties the editor), a
// save carries the version it loaded and takes the server's answer, every
// control locks while a save runs, and moves / removes / Save wait for uploads.
// ---------------------------------------------------------------------------

export default function AboutPageEditor() {
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();

  const [sections, setSections] = useState<AboutSection[]>([]);
  const [loading, setLoading] = useState(true);
  // True once a load succeeded: until then Save and Add stay off.
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // The version token from GET (null = never saved; undefined = an older API).
  const [token, setToken] = useState<string | null | undefined>(undefined);
  // What the server holds (as a save payload): the base of the dirty check.
  const [savedSnapshot, setSavedSnapshot] = useState('[]');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [uploads, setUploads] = useState(0);
  const [confirmEmptyOpen, setConfirmEmptyOpen] = useState(false);
  const [draftOffer, setDraftOffer] = useState<AboutDraft | null>(null);
  const [draftNote, setDraftNote] = useState<string | null>(null);
  const [addAnchor, setAddAnchor] = useState<null | HTMLElement>(null);

  const dirty = loaded && isDirty(sections, savedSnapshot);
  const uploading = uploads > 0;
  // Structure changes (move, remove, add, toggle) and Save wait for uploads.
  const locked = !loaded || saving || uploading;

  useLeaveGuard(dirty, LEAVE_TEXT);
  useAboutDraft({ enabled: loaded && !draftOffer, dirty, baseUpdatedAt: token, sections });

  const beginUpload = useCallback(() => {
    setUploads((n) => n + 1);
    let ended = false;
    return () => {
      if (ended) return;
      ended = true;
      setUploads((n) => Math.max(0, n - 1));
    };
  }, []);

  const load = useCallback(async (opts: { dropDraft?: boolean } = {}) => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await axiosInstance.get(APIS.ABOUT_PAGE.GET);
      const answer = readAnswer(res.data);
      const snap = snapshotOf(answer.sections);
      setSections(answer.sections);
      setToken(answer.updatedAt);
      setSavedSnapshot(snap);
      setSaveError(null);
      setConflict(false);
      setLoaded(true);
      const draft = opts.dropDraft ? null : readDraft();
      const status = draftStatus(draft, answer.updatedAt, snap);
      if (status === 'restore') setDraftOffer(draft);
      else {
        if (status === 'stale' && draft) {
          setDraftNote(
            `Your unsaved changes from ${draftTime(draft)} were made on an older version of the page, so they were not restored.`
          );
        }
        setDraftOffer(null);
        clearDraft();
      }
    } catch {
      // Never empty the editor: what is on screen (if anything) stays as it is.
      setLoadError(LOAD_FAILED_TEXT);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (confirmEmpty = false) => {
    if (locked) return;
    if (!confirmEmpty && needsEmptyConfirm(sections, savedSnapshot)) {
      setConfirmEmptyOpen(true);
      return;
    }
    setSaving(true);
    setSaveError(null);
    setConflict(false);
    try {
      const res = await axiosInstance.patch(APIS.ABOUT_PAGE.UPDATE, savePayload(sections, token, confirmEmpty));
      const body = res.data?.data ?? res.data;
      clearDraft();
      if (Array.isArray(body?.sections)) {
        // The server's normalised page replaces the screen; no re-read.
        const answer = readAnswer(body);
        const next = adoptCids(sections, answer.sections);
        setSections(next);
        setSavedSnapshot(snapshotOf(next));
        setToken(answer.updatedAt);
      } else {
        await load({ dropDraft: true });
      }
      enqueueSnackbar(savedText(body?.revalidated));
    } catch (e) {
      // Local edits stay on screen (and in the draft) until the user reloads.
      setSaveError(aboutErrorText(e));
      setConflict(isAboutChanged(e));
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Discard your unsaved changes?')) return;
    try {
      setSections(withCids(JSON.parse(savedSnapshot)));
    } catch {
      setSections([]);
    }
    setSaveError(null);
    setConflict(false);
    clearDraft();
  };

  const restoreDraft = () => {
    if (draftOffer) setSections(withCids(draftOffer.sections));
    setDraftOffer(null);
  };

  const dropDraft = () => {
    clearDraft();
    setDraftOffer(null);
  };

  const addBlock = (type: string) => {
    setAddAnchor(null);
    if (locked) return;
    setSections((prev) => addSection(prev, type));
  };

  const usableTypes = useMemo(() => SCHEMAS, []);

  return (
    <Page title="About Page">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="About Page"
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'About Page' }]}
          action={
            <Stack direction="row" spacing={1.5}>
              <Button variant="outlined" disabled={locked} startIcon={<Iconify icon="eva:plus-fill" />} onClick={(e) => setAddAnchor(e.currentTarget)}>
                Add section
              </Button>
              <Button variant="contained" onClick={() => save()} disabled={locked || loading || !dirty} startIcon={saving ? <CircularProgress size={16} /> : <Iconify icon="eva:save-outline" />}>
                {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
              </Button>
            </Stack>
          }
        />

        <Menu anchorEl={addAnchor} open={!!addAnchor} onClose={() => setAddAnchor(null)}>
          {usableTypes.map((s) => (
            <MenuItem key={s.type} onClick={() => addBlock(s.type)}>
              <Iconify icon={s.icon} sx={{ mr: 1.5, width: 20, height: 20 }} />
              {s.label}
            </MenuItem>
          ))}
        </Menu>

        <Card sx={{ p: 2.5, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Build the public <b>About Us</b> page from these blocks. Change the order with the arrows, hide a block with its
            toggle, and remove any you don’t need. <b>Changes go live only after you press Save.</b> When there are <b>no</b>{' '}
            sections here, the site shows its original built-in About layout. The Impact Numbers band is managed under{' '}
            <b>Home Statistics</b> and always shows below.
          </Typography>
        </Card>

        {draftOffer && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Stack direction="row" spacing={1}>
                <Button color="inherit" size="small" onClick={dropDraft}>
                  Discard
                </Button>
                <Button color="inherit" size="small" variant="outlined" onClick={restoreDraft}>
                  Restore
                </Button>
              </Stack>
            }
          >
            Restore your unsaved changes from {draftTime(draftOffer)}?
          </Alert>
        )}
        {draftNote && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setDraftNote(null)}>
            {draftNote}
          </Alert>
        )}

        {!loaded ? (
          loading ? (
            <Stack alignItems="center" sx={{ py: 8 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <Card sx={{ p: 6, textAlign: 'center' }}>
              <Iconify icon="eva:alert-triangle-outline" sx={{ width: 48, height: 48, color: 'error.main', mb: 1 }} />
              <Typography variant="h6">{loadError || LOAD_FAILED_TEXT}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Saving stays off until the page has loaded.
              </Typography>
              <Button variant="contained" startIcon={<Iconify icon="eva:refresh-outline" />} onClick={() => load()}>
                Retry
              </Button>
            </Card>
          )
        ) : (
          <>
            {loadError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => load({ dropDraft: true })} disabled={loading}>
                    Retry
                  </Button>
                }
              >
                {loadError}
              </Alert>
            )}
            {saveError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={conflict ? undefined : () => setSaveError(null)}
                action={
                  conflict ? (
                    <Button color="inherit" size="small" onClick={() => load({ dropDraft: true })} disabled={loading || saving}>
                      Reload their version
                    </Button>
                  ) : undefined
                }
              >
                {saveError}
              </Alert>
            )}

            {(dirty || uploading) && (
              <Card
                sx={{
                  p: 1.5,
                  mb: 2,
                  position: 'sticky',
                  top: 72,
                  zIndex: 10,
                  borderLeft: '4px solid',
                  borderColor: 'warning.main',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography variant="subtitle2" sx={{ flexGrow: 1 }} role="status">
                    {uploading ? UPLOAD_WAIT_TEXT : saving ? 'Saving…' : 'Unsaved changes'}
                  </Typography>
                  <Button size="small" color="inherit" onClick={discard} disabled={locked || !dirty}>
                    Discard
                  </Button>
                  <Button size="small" variant="contained" onClick={() => save()} disabled={locked || !dirty}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </Stack>
              </Card>
            )}

            {sections.length === 0 ? (
              <Card sx={{ p: 6, textAlign: 'center' }}>
                <Iconify icon="eva:layout-outline" sx={{ width: 48, height: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="h6">No sections yet</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  The site is showing its default About layout. Add sections to take over the page.
                </Typography>
                <Button variant="contained" disabled={locked} startIcon={<Iconify icon="eva:plus-fill" />} onClick={(e) => setAddAnchor(e.currentTarget)}>
                  Add your first section
                </Button>
              </Card>
            ) : (
              <Stack spacing={2}>
                {sections.map((section, idx) => (
                  <SectionCard
                    key={section._cid}
                    section={section}
                    index={idx}
                    count={sections.length}
                    locked={locked}
                    lockNote={uploading ? UPLOAD_WAIT_TEXT : saving ? 'Saving…' : ''}
                    saving={saving}
                    beginUpload={beginUpload}
                    setSections={setSections}
                  />
                ))}
              </Stack>
            )}
          </>
        )}
      </Container>

      <ConfirmationDialog
        isOpen={confirmEmptyOpen}
        onClose={() => setConfirmEmptyOpen(false)}
        confirmAction={() => save(true)}
        headText="Remove every block?"
        text={EMPTY_CONFIRM_TEXT}
        loading={saving}
      />
    </Page>
  );
}
